import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, Chip,
  CircularProgress, TextField, InputAdornment, IconButton, Tooltip, Divider, Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { getSaleHistory, updateSaleEntry, deleteSaleEntry } from '../../databaseManager';
import { channelLabel, formatCurrency, parseStoredDate, formatStoredDate } from '../products/productUtils';

const parseOptional = (s) => {
  if (s === '' || s == null) return null;
  const n = parseFloat(s);
  return Number.isNaN(n) ? null : n;
};

/**
 * Every entry in a product's saleHistory (web port of iOS SaleHistoryView).
 * Price and cost are editable on any entry; deltas roll into the product's
 * totals. Only off-platform (manual) sales can be deleted — a channel sale has
 * a real order behind it.
 *
 * Props: open, onClose, customer (id), product, onChanged()
 */
export default function SaleHistoryDialog({ open, onClose, customer, product, onChanged }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // { id, unitPrice, unitCost }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!product) return;
    setLoading(true);
    setError(null);
    try {
      setEntries(await getSaleHistory(customer, product.productUri, parseStoredDate));
    } catch (err) {
      console.error('Failed to load sale history:', err);
      setError('Could not load sale history.');
    } finally {
      setLoading(false);
    }
  }, [customer, product]);

  useEffect(() => {
    if (open) {
      setEditing(null);
      load();
    }
  }, [open, load]);

  const saveEdit = async (entry) => {
    setBusy(true);
    try {
      const updated = await updateSaleEntry(
        customer, product.productUri, entry,
        { unitPrice: parseOptional(editing.unitPrice), unitCost: parseOptional(editing.unitCost) },
        entries[0]?.id === entry.id,
      );
      setEntries((prev) => prev.map((e) => (e.id === entry.id ? updated : e)));
      setEditing(null);
      onChanged();
    } catch (err) {
      console.error('Failed to update sale:', err);
      setError('Could not update this sale.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (entry) => {
    if (!window.confirm('Delete this sale? Its quantity goes back into stock.')) return;
    setBusy(true);
    try {
      const remaining = entries.filter((e) => e.id !== entry.id);
      await deleteSaleEntry(customer, product.productUri, entry, remaining, formatStoredDate);
      setEntries(remaining);
      onChanged();
    } catch (err) {
      console.error('Failed to delete sale:', err);
      setError('Could not delete this sale.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>
        Sale history
        <Typography variant="body2" color="text.secondary" noWrap>
          {product?.specs?.Title || product?.validatedTitle || 'Untitled'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
        ) : entries.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No sale history recorded for this item.</Typography>
        ) : entries.map((entry, idx) => {
          const isEditing = editing?.id === entry.id;
          const isOffPlatform = !entry.channel || entry.channel === 'off_platform';
          return (
            <Box key={entry.id}>
              {idx > 0 && <Divider sx={{ my: 1.5 }} />}
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={channelLabel(entry.channel)} sx={{ bgcolor: 'grey.100' }} />
                    <Typography variant="body2">
                      {entry.soldAtDate ? entry.soldAtDate.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                    </Typography>
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                    Qty sold: {entry.quantitySold ?? 0}
                    {entry.orderId ? ` · Order ${entry.orderId}` : ''}
                  </Typography>
                  {entry.notes && <Typography variant="caption" color="text.secondary" component="div">{entry.notes}</Typography>}
                </Box>
                {!isEditing && (
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {entry.unitPrice != null ? `${formatCurrency(entry.unitPrice)} each` : 'No price'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {entry.unitCost != null ? `Cost ${formatCurrency(entry.unitCost)} each` : 'No cost'}
                    </Typography>
                  </Box>
                )}
                {!isEditing && (
                  <Box sx={{ display: 'flex' }}>
                    <Tooltip title="Edit price / cost">
                      <IconButton size="small" disabled={busy}
                        onClick={() => setEditing({ id: entry.id, unitPrice: entry.unitPrice ?? '', unitCost: entry.unitCost ?? '' })}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isOffPlatform && (
                      <Tooltip title="Delete sale">
                        <IconButton size="small" disabled={busy} onClick={() => remove(entry)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                )}
              </Box>
              {isEditing && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                  <TextField size="small" type="number" label="Sale price (each)" value={editing.unitPrice}
                    onChange={(e) => setEditing((prev) => ({ ...prev, unitPrice: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                  <TextField size="small" type="number" label="Cost (each)" value={editing.unitCost}
                    onChange={(e) => setEditing((prev) => ({ ...prev, unitCost: e.target.value }))}
                    InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} />
                  <Button size="small" variant="contained" disabled={busy} onClick={() => saveEdit(entry)}>Save</Button>
                  <Button size="small" disabled={busy} onClick={() => setEditing(null)}>Cancel</Button>
                </Stack>
              )}
            </Box>
          );
        })}
        {error && <Typography variant="body2" color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
